import {useEffect, useState} from 'react';
import {hashKey, useSuspenseQuery} from '@tanstack/react-query';
import {isServer} from 'coreUtils/utils';
import {registerModuleStateReset} from 'coreUtils/moduleState';

import useRefetchOnMount from './useRefetchOnMount';
import useShellGate from './useShellGate';

interface UseServerDataOptions {
	/** How long (ms) fetched data is considered fresh before background refetch */
	staleTime?: number;
	/** How long (ms) unused/inactive cache data remains in memory (default: 5 min). Set to 0 to discard immediately on unmount. */
	gcTime?: number;
}

let isFirstLoad = !isServer;

registerModuleStateReset(() => {
	isFirstLoad = !isServer;
});

/**
 * Ends the hydration phase: queries mounted afterwards refetch stale data on mount.
 *
 * Called when the page (`withSSRPage`) has mounted and on the first client navigation, NOT by the
 * first `useServerData` consumer: with nested lazy chunks the page hydrates in a later pass than
 * its parents (e.g. `APIRouter`), and must still reuse the server data without refetching.
 */
export const markServerDataHydrated = (): void => {
	isFirstLoad = false;
};

/**
 * Generic data fetching hook that replaces the static `getProps` pattern.
 * Uses `useSuspenseQuery` so the component suspends until data is ready.
 *
 * On SSR with `renderToPipeableStream`, the server waits for all Suspense
 * boundaries to resolve before sending the response. The query result is
 * then dehydrated into `__PRELOADED_STATE__.__reactQuery` and rehydrated
 * on the client via `HydrationBoundary`, avoiding a re-fetch.
 *
 * During CSR navigation, `startTransition` wraps all history listener
 * notifications (via `TransitionalRouter`).
 * When `useSuspenseQuery` suspends with a new query key (no cache), React
 * keeps the previously committed UI visible until the fetch resolves.
 * This means the old page stays on screen — no skeleton flash — until the
 * new page has all its data ready.
 *
 * @param key     - Unique cache key for this query
 * @param fn      - Async function that fetches the data
 * @param deps    - Additional dependencies appended to the query key for cache invalidation
 * @param options - Optional React Query overrides (e.g. staleTime)
 */
const useServerData = <T, >(
	key: string | null,
	fn: () => Promise<T>,
	deps: unknown[] = [],
	options?: UseServerDataOptions,
): T => {
	const [isHydrating, setIsHydrating] = useState(isFirstLoad);
	const refetchOnMount = useRefetchOnMount(!isHydrating);

	useEffect(() => {
		setIsHydrating(false);
	}, []);

	const queryKey = [key, ...deps].filter(item => item != null);
	// Server: the shell waits for this data (pending until the component renders with it).
	const gate = useShellGate();
	const gateId = gate && `data:${hashKey(queryKey)}`;

	gate?.begin(gateId);

	const {data} = useSuspenseQuery<T>({
		queryKey,
		queryFn: fn,
		staleTime: options?.staleTime,
		gcTime: options?.gcTime,
		refetchOnMount,
	});

	gate?.end(gateId);

	return data;
};

export default useServerData;
